import React, { useState, useEffect, useRef } from 'react';
import { formatNumber, getPriceChangeColor, formatPairName } from '../../utils/formatters';
import useTradingStore from '../../store/useTradingStore';
import { ChevronDownIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import API_CONFIG from '../../config/api';

const MarketTicker = () => {
  const [favorites, setFavorites] = useState(['BTC-USDT']);
  const [showPairDropdown, setShowPairDropdown] = useState(false);
  const [marketData, setMarketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const {
    selectedPair,
    setSelectedPair,
  } = useTradingStore();

  // Fetch market data from new API
  useEffect(() => {
    const fetchMarketData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_CONFIG.BASE_URL}/v1/homepage-external-price/`);
        const data = await response.json();
        setMarketData(data);
      } catch (error) {
        console.error('Error fetching market data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
    
    // Set up polling to refresh data every 30 seconds
    const interval = setInterval(fetchMarketData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowPairDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Calculate dropdown position when opening
  const handleDropdownToggle = () => {
    if (!showPairDropdown && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: buttonRect.bottom + 8, // 8px gap below button
        left: buttonRect.left,
        width: buttonRect.width
      });
    }
    setShowPairDropdown(!showPairDropdown);
  };

  // Get current pair data from market data
  const getCurrentPairData = () => {
    if (!marketData || !marketData.pairs_data) return null;
    return marketData.pairs_data[selectedPair] || null;
  };

  const pairData = getCurrentPairData();
  
  // Get quote currency from pair data
  const getQuoteCurrency = (pairCode) => {
    if (!marketData || !marketData.pairs_data || !marketData.pairs_data[pairCode]) return 'USDT';
    return marketData.pairs_data[pairCode].pair_data?.quote?.code || 'USDT';
  };
  
  // Get all available pairs from market data
  const availablePairs = marketData ? Object.keys(marketData.pairs_data || {}) : [];

  const toggleFavorite = (pair) => {
    setFavorites(prev =>
      prev.includes(pair)
        ? prev.filter(p => p !== pair)
        : [...prev, pair]
    );
  };

  const handlePairSelect = (pair) => {
    setSelectedPair(pair);
    setShowPairDropdown(false);
  };

  // Show loading state if market data is not loaded yet
  if (loading || !marketData) {
    return (
      <div className="my-2">
        <div className="flex items-center h-12 sm:h-16 px-2 sm:px-3">
          <div className="flex items-center space-x-3 sm:space-x-6 min-w-0 flex-1">
            <div className="text-white text-sm sm:text-base">Loading market data...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-2 relative">
      {/* Main Ticker Bar with Horizontal Scroll */}
      <div className="flex items-center h-12 sm:h-16 px-2 sm:px-3 overflow-x-auto scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        {/* Selected Pair Info */}
        <div className="flex items-center space-x-3 sm:space-x-6 min-w-0 flex-1 min-w-max">
          {/* Pair Selector */}
          <div className="flex items-center space-x-1 sm:space-x-2 relative border border-[#2A356D] rounded-lg p-1 flex-shrink-0">
            <button
              onClick={() => toggleFavorite(selectedPair)}
              className="text-white hover:text-white transition-colors p-1"
            >
              {favorites.includes(selectedPair) ? (
                <StarSolidIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
              ) : (
                <StarIcon className="h-4 w-4 sm:h-5 sm:w-5" />
              )}
            </button>

            {/* Pair Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                ref={buttonRef}
                onClick={handleDropdownToggle}
                className="flex items-center space-x-1 sm:space-x-2 text-base sm:text-xl font-bold text-white hover:bg-gray-700 py-1 px-1 sm:px-2 rounded transition-colors"
              >
                <span className="truncate max-w-20 sm:max-w-none">{formatPairName(selectedPair || 'BTC-USDT')}</span>
                <ChevronDownIcon className={`h-4 w-4 sm:h-5 sm:w-4 text-white font-bold transition-transform ${showPairDropdown ? 'rotate-180' : ''}`} />
              </button>

              {showPairDropdown && (
                <div className="fixed inset-0 z-[9999]">
                  {/* Backdrop */}
                  <div 
                    className="absolute inset-0 bg-black/20"
                    onClick={() => setShowPairDropdown(false)}
                  />
                  
                  {/* Dropdown Content */}
                  <div 
                    className="absolute bg-gray-900 border border-gray-700 rounded-lg shadow-2xl max-h-80 sm:max-h-96 overflow-y-auto z-[10000]"
                    style={{
                      top: `${dropdownPosition.top}px`,
                      left: `${dropdownPosition.left}px`,
                      width: `${Math.max(dropdownPosition.width, 320)}px`, // Increased minimum width to 320px
                      maxWidth: 'calc(100vw - 32px)' // Prevent overflow on small screens
                    }}
                  >
                    {availablePairs.length > 0 ? (
                      availablePairs.map((pairCode) => {
                        const pairMarketData = marketData.pairs_data[pairCode];
                        return (
                          <button
                            key={pairCode}
                            onClick={() => handlePairSelect(pairCode)}
                            className={`w-full text-left px-3 sm:px-4 py-3 sm:py-4 hover:bg-gray-700 transition-colors ${selectedPair === pairCode ? 'bg-gray-700 text-white' : 'text-white'
                              }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-sm sm:text-base truncate">{formatPairName(pairCode)}</div>
                              </div>
                              {pairMarketData && (
                                <div className="flex flex-col items-end ml-3 flex-shrink-0">
                                  <div className="text-white text-xs sm:text-sm font-medium">
                                    {formatNumber(pairMarketData.price, 2)} {getQuoteCurrency(pairCode)}
                                  </div>
                                  <div className={`text-xs ${getPriceChangeColor(pairMarketData.price_24h_value / 100)}`}>
                                    {pairMarketData.price_24h_value >= 0 ? '+' : ''}
                                    {formatNumber(pairMarketData.price_24h_value, 2)}%
                                  </div>
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })
                    ) : (
                      <div className="px-4 py-3 text-white text-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b border-blue-500 mx-auto mb-2"></div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Price Info */}
          {pairData ? (
            <>
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <div>
                  <div className="text-lg sm:text-2xl font-bold text-white">
                    {formatNumber(pairData.price, 2)} {getQuoteCurrency(selectedPair)}
                  </div>
                  <div className={`text-xs sm:text-sm ${getPriceChangeColor(pairData.price_24h_value / 100)}`}>
                    {pairData.price_24h_value >= 0 ? '+' : ''}
                    {formatNumber(pairData.price_24h_value, 2)}%
                  </div>
                </div>
              </div>

              {/* 24h Stats - Hidden on mobile, shown on larger screens */}
              <div className="hidden md:flex items-center space-x-8 lg:space-x-20 text-xs sm:text-sm flex-shrink-0">
                <div className="text-white">
                  <div className="text-green-500 font-bold text-xs sm:text-sm">24h High</div>
                  <div className="text-white font-medium text-xs sm:text-sm">
                    {formatNumber(pairData.price, 2)} {getQuoteCurrency(selectedPair)}
                  </div>
                </div>
                <div className="text-white">
                  <div className="text-red-500 font-bold text-xs sm:text-sm">24h Low</div>
                  <div className="text-white font-medium text-xs sm:text-sm">
                    {formatNumber(pairData.price, 2)} {getQuoteCurrency(selectedPair)}
                  </div>
                </div>
                <div className="text-white hidden lg:block">
                  <div className="text-white font-bold text-xs sm:text-sm">24h Volume ({selectedPair?.split('-')[0]})</div>
                  <div className="text-white font-medium text-xs sm:text-sm">
                    {formatNumber(pairData.volume || 0, 2)}
                  </div>
                </div>
                <div className="text-white hidden lg:block">
                  <div className="text-blue-500 font-bold text-xs sm:text-sm">Base Volume</div>
                  <div className="text-white font-medium text-xs sm:text-sm">
                    {formatNumber(pairData.base_volume || 0, 2)}
                  </div>
                </div>
              </div>

              {/* Mobile Stats - Compact view for small screens */}
              <div className="flex md:hidden items-center space-x-3 text-xs flex-shrink-0">
                <div className="text-white">
                  <div className="text-green-500 font-bold">H</div>
                  <div className="text-white font-medium">
                    {formatNumber(pairData.price, 2)} {getQuoteCurrency(selectedPair)}
                  </div>
                </div>
                <div className="text-white">
                  <div className="text-red-500 font-bold">L</div>
                  <div className="text-white font-medium">
                    {formatNumber(pairData.price, 2)} {getQuoteCurrency(selectedPair)}
                  </div>
                </div>
                <div className="text-white">
                  <div className="text-white font-bold">Vol</div>
                  <div className="text-white font-medium">
                    {formatNumber(pairData.volume || 0, 1)}
                  </div>
                </div>
              </div>
            </>
          ) : (
            // Fallback when market data is not available
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              <div className="animate-spin rounded-full h-4 w-4 border-b border-white mx-auto"></div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketTicker;